import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Clock, Loader2, ShieldCheck, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { fmtDate } from "@/components/trial/TrialGate";

const POINTS = [
  [Sparkles, "#FFDF00", "Toutes les matières, tous les chapitres, à son rythme", "Every subject and chapter, at their own pace"],
  [BookOpen, "#B7F000", "Le kit Lecture avec Kiko, anglais et français", "The Reading kit with Kiko, English & French"],
  [Star, "#FF9500", "Les étoiles, les avatars et les récompenses", "Stars, avatars and rewards"],
];

// Parent activates the 10-minute full-access trial for one child
export default function TrialStart({ kid = false }) {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/children/${childId}/trial`).then(({ data }) => setSt(data)).catch(() => navigate("/parent")); }, [childId, navigate]);
  useEffect(() => { if (st && (st.state === "paid" || st.state === "active")) navigate(`/kid/${childId}`, { replace: true }); }, [st, childId, navigate]);
  if (!st) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" size={40} /></div>;
  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/children/${childId}/trial/start`);
      navigate(`/kid/${childId}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      setBusy(false);
    }
  };
  const blocked = st.state === "ended" || st.state === "grade_used" || st.state === "former";
  return (
    <div className="min-h-screen bg-[#FDFCF0] dot-grid-soft font-parent text-slate-900">
      <div className="max-w-4xl mx-auto px-5 py-8">
        <button onClick={() => navigate(kid ? `/kid/${childId}` : "/parent")} className="w-11 h-11 rounded-full bg-white border-[3px] border-slate-900 flex items-center justify-center shadow-[3px_3px_0_0_#111]" data-testid="demo-back"><ArrowLeft size={18} /></button>
        <div className="mt-6 grid md:grid-cols-[auto_1fr] gap-6 items-center">
          <motion.img src="/mascot/kiko.png" alt="Kiko" className="w-36 sm:w-44 mx-auto" animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 2.6 }} />
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-900 bg-[#FFDF00] px-3 py-1 text-xs font-black uppercase tracking-[0.18em]"><Clock size={14} /> 10 min · Essai gratuit · Free trial</span>
            <h1 className="font-kids text-4xl sm:text-5xl font-black tracking-tight mt-3 leading-tight" data-testid="trial-title">
              {blocked ? "Essai déjà utilisé" : `10 minutes d'accès complet pour ${st.child_name}`}
            </h1>
            <p className="font-kids text-lg font-bold text-slate-600 mt-1">{blocked ? "Free trial already used" : `10 minutes of full access for ${st.child_name}`}</p>
          </div>
        </div>
        {blocked ? (
          <div className="mt-8 rounded-3xl border-4 border-slate-900 bg-white p-6 shadow-[6px_6px_0_0_#111]" data-testid="trial-blocked">
            <p>{st.state === "former" ? `L'essai gratuit est réservé aux enfants qui n'ont jamais eu de formule.` : st.state === "grade_used" ? `L'essai gratuit a déjà été utilisé pour le Grade ${st.grade} sur ce compte.` : `L'essai gratuit de ${st.child_name} est terminé.`} Choisissez une formule pour continuer.</p>
            <p className="text-slate-500 text-sm mt-1">{st.state === "former" ? "The free trial is only for children who never had a plan." : st.state === "grade_used" ? `The free trial was already used for Grade ${st.grade} on this account.` : "The free trial is over."} Choose a plan to continue.</p>
            {st.state === "ended" && !st.reset && st.keep_until && <p className="mt-3 text-sm font-semibold">Payez avant le {fmtDate(st.keep_until)} pour garder sa progression.</p>}
            <button onClick={() => navigate(`/pricing?trial_ended=${childId}`)} data-testid="trial-blocked-plans"
              className="mt-5 rounded-full border-[3px] border-slate-900 bg-[#B7F000] px-8 py-3 font-kids font-black text-lg shadow-[4px_4px_0_0_#111]">Choisir une formule · Choose a plan</button>
          </div>
        ) : (
          <>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {POINTS.map(([Icon, color, fr, en], k) => (
                <motion.div key={en} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + k * 0.08 }}
                  className="rounded-3xl border-4 border-slate-900 bg-white p-5 shadow-[6px_6px_0_0_#111]">
                  <span className="w-12 h-12 rounded-2xl border-[3px] border-slate-900 flex items-center justify-center" style={{ background: color }}><Icon size={22} /></span>
                  <b className="font-kids text-lg block mt-3 leading-tight">{fr}</b>
                  <span className="text-sm text-slate-500">{en}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 rounded-3xl border-4 border-slate-900 bg-yellow-100 p-5 text-sm space-y-2" data-testid="trial-rules">
              <p className="font-bold flex items-center gap-2"><ShieldCheck size={18} /> À savoir pour les parents · For parents</p>
              <p>• Le chrono de 10 minutes démarre dès que vous cliquez et ne s'arrête pas, même en pause. / The 10-minute timer starts when you click and keeps running.</p>
              <p>• Un seul essai par enfant et par Grade sur votre compte. / One trial per child and per Grade on your account.</p>
              <p>• Après 10 minutes, l'accès se bloque : choisissez une formule. Si vous payez dans les 5 jours, {st.child_name} garde toute sa progression ; sinon elle est effacée. / Pay within 5 days to keep the progress.</p>
            </div>
            {kid ? (
              <button onClick={() => navigate(`/parent/trial/${childId}`)} data-testid="trial-start-button"
                className="mt-7 w-full sm:w-auto rounded-full border-[3px] border-slate-900 bg-[#B7F000] px-10 py-4 font-kids font-black text-xl shadow-[4px_4px_0_0_#111] hover:translate-y-[2px] hover:shadow-[2px_2px_0_0_#111] transition-transform transition-shadow">
                Parents : activer avec votre code PIN · Parents: activate with your PIN
              </button>
            ) : (
            <button onClick={start} disabled={busy} data-testid="trial-start-button"
              className="mt-7 w-full sm:w-auto rounded-full border-[3px] border-slate-900 bg-[#B7F000] px-10 py-4 font-kids font-black text-2xl shadow-[4px_4px_0_0_#111] hover:translate-y-[2px] hover:shadow-[2px_2px_0_0_#111] transition-transform transition-shadow disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 className="animate-spin" />} Démarrer les 10 minutes · Start
            </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
